import { useState, useEffect, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveImage, hexToRgba, getCardGap, getTextAlign } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';
function AnimatedCounter({ value, visible, enabled = true, duration: dur }) {
    const [display, setDisplay] = useState(enabled ? '0' : value);
    const numericMatch = typeof value === 'string' ? value.match(/^([+-]?)(\d+(?:\.\d+)?)(.*)$/) : null;

    useEffect(() => {
        if (!enabled || !visible || !numericMatch) { setDisplay(value); return; }
        const prefix = numericMatch[1];
        const target = parseFloat(numericMatch[2]);
        const suffix = numericMatch[3]; // e.g. '%', 'K', 'M+', etc.
        const isFloat = numericMatch[2].includes('.');
        const decimals = isFloat ? numericMatch[2].split('.')[1].length : 0;
        const duration = dur || 1800;
        const start = performance.now();
        let raf;
        const step = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = target * eased;
            setDisplay(prefix + (isFloat ? current.toFixed(decimals) : Math.round(current).toLocaleString()) + suffix);
            if (progress < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [visible, enabled, value, dur]);

    return display;
}

export default function StatsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const items = rawItems.length > 0 ? rawItems : [
        { value: '500+', label: 'Clientes satisfechos' }, { value: '98%', label: 'Satisfaccion' },
        { value: '10+', label: 'Anos de experiencia' }, { value: '24/7', label: 'Soporte disponible' },
    ];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const columns = config.columns || 4;
    const cols = columns === 2 ? 'grid-cols-2' : columns === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4';
    const Header = () => content.title ? <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} /> : null;

    // ── Counter-strip variant ──
    if (variant === 'counter-strip') {
        const stripBg = config.strip_color === 'secondary' ? s.secondary : s.accent;
        const stripTextColor = '#ffffff';
        return (
            <section className={`relative ${s.radiusClass} ${s.shadowClass}`}
                style={{ backgroundColor: stripBg, maxHeight: 120 }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 ${anim}`}>
                    <div className="flex flex-wrap items-center justify-center">
                        {items.map((item, i) => (
                            <div key={i} className={`flex-1 min-w-[120px] text-center px-4 md:px-6 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                                style={{
                                    transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                    borderRight: i < items.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
                                }}>
                                <div className="text-2xl md:text-3xl font-extrabold" style={{ color: stripTextColor }}>
                                    {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                </div>
                                <p className="text-[11px] sm:text-xs font-medium mt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Image overlay variant ──
    if (variant === 'image-overlay') {
        const imgUrl = resolveImage(content, 'image', mediaFolders);
        return (
            <section className={`relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`} style={{ minHeight: 280 }}>
                {imgUrl ? (
                    <img src={imgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy"/>
                ) : (
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.8)})` }}/>
                )}
                <div className="absolute inset-0 bg-black/60"/>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 ${anim}`}>
                    {content.title && (
                        <div className="text-center mb-10">
                            {content.tag && <span className="text-sm font-medium text-white/60 tracking-wider uppercase">{content.tag}</span>}
                            <h2 className="text-2xl md:text-3xl font-bold text-white mt-1">{content.title}</h2>
                            {content.subtitle && <p className="text-white/70 mt-2">{content.subtitle}</p>}
                        </div>
                    )}
                    <div className={`grid ${cols} gap-8 md:gap-12`}>
                        {items.map((item, i) => (
                            <div key={i} className={`text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                <div className="text-4xl md:text-5xl font-extrabold text-white mb-2">
                                    {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                </div>
                                <p className="text-sm font-medium text-white/80">{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Circular variant ──
    if (variant === 'circular') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className={`grid ${cols} gap-8 md:gap-12`}>
                        {items.map((item, i) => {
                            const pct = Math.min(parseInt(item.value) || 0, 100);
                            const radius = 45;
                            const circumference = 2 * Math.PI * radius;
                            const offset = circumference - (pct / 100) * circumference;
                            return (
                                <div key={i} className={`text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                    <div className="relative inline-block mb-4">
                                        <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                                            <circle cx="50" cy="50" r={radius} fill="none" stroke={hexToRgba(s.accent, 0.08)} strokeWidth="8"/>
                                            <circle cx="50" cy="50" r={radius} fill="none" stroke={s.accent} strokeWidth="8"
                                                strokeLinecap="round"
                                                strokeDasharray={circumference}
                                                strokeDashoffset={visible ? offset : circumference}
                                                className="transition-all ease-out" style={{ transitionDuration: '1500ms' }}/>
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-2xl font-extrabold" style={{ color: s.text }}>
                                                {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-sm font-medium" style={{ color: s.text }}>{item.label}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Icons variant ──
    if (variant === 'icons') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className={`grid ${cols} gap-8`}>
                        {items.map((item, i) => (
                            <div key={i} className={`text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {item.icon && <div className="mb-3" style={{color: item.number_color || s.accent}}><SectionIcon name={item.icon} size={40}/></div>}
                                <div className="text-4xl md:text-5xl font-extrabold" style={{ color: item.number_color || s.accent }}>
                                    {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                </div>
                                <p className="text-sm font-medium mt-2" style={{ color: s.text }}>{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Progress variant ──
    if (variant === 'progress') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="space-y-8">
                        {items.map((item, i) => {
                            const pct = Math.min(parseInt(item.value) || 0, 100);
                            return (
                                <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-sm font-semibold" style={{ color: s.text }}>{item.label}</span>
                                        <span className="text-sm font-bold" style={{ color: s.accent }}>{item.prefix}{item.value}{item.suffix}</span>
                                    </div>
                                    <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                        <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: visible ? `${pct}%` : '0%', backgroundColor: s.accent }}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Horizontal variant ──
    if (variant === 'horizontal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="flex flex-wrap items-center justify-center divide-x" style={{ borderColor: hexToRgba(s.accent, 0.13) }}>
                        {items.map((item, i) => (
                            <div key={i} className={`text-center px-8 md:px-12 py-4 transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                <div className="text-3xl md:text-4xl font-extrabold" style={{ color: s.accent }}>
                                    {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                </div>
                                <p className="text-sm mt-1" style={{ color: s.textSub }}>{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className={`grid ${cols} ${getCardGap(config) || 'gap-6'}`}>
                        {items.map((item, i) => (
                            <div key={i} className={`${getTextAlign(config) || 'text-center'} p-8 rounded-2xl transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ backgroundColor: hexToRgba(s.accent, 0.03), transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {item.icon && <div className="mb-3" style={{color: item.number_color || s.accent}}><SectionIcon name={item.icon} size={36}/></div>}
                                <div className="text-4xl md:text-5xl font-extrabold mb-2" style={{ color: item.number_color || s.accent }}>
                                    {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                </div>
                                <p className="text-sm font-medium" style={{ color: s.text }}>{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Countup variant ──
    if (variant === 'countup') {
        const parseStatValue = (val) => {
            const str = String(val || '0');
            const match = str.match(/^([0-9]*\.?[0-9]+)\s*([+%KMBkm]*)/);
            if (!match) return { target: 0, suffix: '', decimals: 0 };
            const num = parseFloat(match[1]);
            const sfx = match[2] || '';
            const decimals = match[1].includes('.') ? match[1].split('.')[1].length : 0;
            return { target: num, suffix: sfx, decimals };
        };

        const CountUpNumber = ({ item, index }) => {
            const [count, setCount] = useState(0);
            const parsed = parseStatValue(item.value);
            const hasStarted = useRef(false);
            const nodeRef = useRef(null);

            useEffect(() => {
                if (!visible || !enableAnimations || hasStarted.current) {
                    if (!enableAnimations) setCount(parsed.target);
                    return;
                }
                hasStarted.current = true;
                const duration = 2000;
                const startTime = performance.now();
                const step = (now) => {
                    const elapsed = now - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    // ease-out cubic
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setCount(eased * parsed.target);
                    if (progress < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
            }, [visible, enableAnimations, parsed.target]);

            const display = parsed.decimals > 0 ? count.toFixed(parsed.decimals) : Math.round(count);

            return (
                <div ref={nodeRef} className={`text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: visible ? `${index * 150}ms` : '0ms' }}>
                    <div className="text-5xl md:text-6xl font-extrabold mb-2" style={{ color: s.accent }}>
                        {item.prefix}{display}{parsed.suffix}
                    </div>
                    <p className="text-sm font-medium" style={{ color: s.textSub }}>{item.label}</p>
                </div>
            );
        };

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className={`grid ${cols} gap-8 md:gap-12`}>
                        {items.map((item, i) => <CountUpNumber key={i} item={item} index={i}/>)}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Glassmorphism variant ──
    if (variant === 'glassmorphism') {
        return (
            <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`}
                style={{ background: `linear-gradient(135deg, #0f172a, #1e293b)` }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && (
                        <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={{
                            ...s,
                            text: '#ffffff',
                            textSub: 'rgba(255,255,255,0.6)',
                        }}/>
                    )}
                    <div className={`grid ${cols} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i}
                                className={`group text-center p-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl
                                    transition-all duration-500 hover:bg-white/10 hover:border-white/20 hover:shadow-lg
                                    ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{
                                    transitionDelay: visible ? `${i * 120}ms` : '0ms',
                                    boxShadow: `0 0 0 transparent`,
                                }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 30px ${hexToRgba(s.accent, 0.15)}`}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = `0 0 0 transparent`}
                            >
                                {item.icon && (
                                    <div className="mb-4 flex justify-center" style={{ color: s.accent }}>
                                        <SectionIcon name={item.icon} size={36}/>
                                    </div>
                                )}
                                <div className="text-4xl md:text-5xl font-extrabold mb-2 text-white">
                                    <span style={{ color: s.accent }}>{item.prefix}</span>
                                    <AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>
                                    <span style={{ color: s.accent }}>{item.suffix}</span>
                                </div>
                                <p className="text-sm font-medium text-white/70">{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Progress-bars variant (animated progress bars with circular gauge option) ──
    if (variant === 'progress-bars') {
        const gaugeMode = config.gauge_mode || 'bar'; // 'bar' or 'circle'

        if (gaugeMode === 'circle') {
            return (
                <section className={cls} style={bgS}>
                    <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                    <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                        <Header/>
                        <div className={`grid ${cols} gap-8`}>
                            {items.map((item, i) => {
                                const pct = Math.min(parseFloat(item.value) || 0, 100);
                                const radius = 50;
                                const circumference = 2 * Math.PI * radius;
                                const offset = circumference - (pct / 100) * circumference;
                                return (
                                    <div key={i} className={`text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                        style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                        <div className="relative inline-block mb-3">
                                            <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                                                <circle cx="60" cy="60" r={radius} fill="none" stroke={hexToRgba(s.accent, 0.1)} strokeWidth="10"/>
                                                <circle cx="60" cy="60" r={radius} fill="none"
                                                    stroke={`url(#gradient-${i})`}
                                                    strokeWidth="10" strokeLinecap="round"
                                                    strokeDasharray={circumference}
                                                    strokeDashoffset={visible ? offset : circumference}
                                                    className="transition-all ease-out" style={{ transitionDuration: '1800ms' }}/>
                                                <defs>
                                                    <linearGradient id={`gradient-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                                        <stop offset="0%" stopColor={s.accent}/>
                                                        <stop offset="100%" stopColor={s.secondary || hexToRgba(s.accent, 0.6)}/>
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="text-2xl font-extrabold" style={{ color: s.accent }}>
                                                    {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-sm font-medium" style={{ color: s.text }}>{item.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
                </section>
            );
        }

        // Default: bar mode
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="space-y-8">
                        {items.map((item, i) => {
                            const pct = Math.min(parseFloat(item.value) || 0, 100);
                            return (
                                <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                    <div className="flex justify-between mb-2">
                                        <span className="text-sm font-semibold" style={{ color: s.text }}>{item.label}</span>
                                        <span className="text-lg font-bold" style={{ color: s.accent }}>
                                            {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                        </span>
                                    </div>
                                    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                        <div className="h-full rounded-full transition-all ease-out"
                                            style={{
                                                width: visible ? `${pct}%` : '0%',
                                                transitionDuration: '1800ms',
                                                background: `linear-gradient(90deg, ${s.accent}, ${s.secondary || hexToRgba(s.accent, 0.7)})`,
                                            }}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Change badge helper ──
    const ChangeBadge = ({item}) => {
        if (!item.change) return null;
        const type = item.change_type || 'neutral';
        const colors = type === 'positive' ? 'bg-green-50 text-green-600' : type === 'negative' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500';
        return <span className={`inline-block ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${colors}`}>{item.change}</span>;
    };

    // ── Simple (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <Header/>
                <div className={`grid ${cols} ${getCardGap(config) || 'gap-8 md:gap-12'}`}>
                    {items.map((item, i) => (
                        <div key={i} className={`${getTextAlign(config) || 'text-center'} transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                            <div className="text-4xl md:text-5xl font-extrabold mb-2" style={{ color: item.number_color || s.accent }}>
                                {item.prefix}<AnimatedCounter value={item.value} visible={visible} enabled={config.count_up} duration={config.count_duration}/>{item.suffix}
                                <ChangeBadge item={item}/>
                            </div>
                            <p className="text-sm font-medium" style={{ color: s.text }}>{item.label}</p>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
